import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import { ScenarioType } from 'shared/enums/scenario-type';
import { jsonParseWithLowerCaseKeys } from 'shared/utils/string-utils';

import AcousticConfiguration from 'features/residents/models/configurations/acoustic-configuration';
import AudioConfiguration from 'features/residents/models/configurations/audio-configuration';
import BaseAlarmOnlyScenarioConfiguration from 'features/residents/models/configurations/base-alarm-only-scenario-configuration';
import BaseAlarmScenarioConfiguration from 'features/residents/models/configurations/base-alarm-scenario-configuration';
import BaseSettingScenarioConfiguration from 'features/residents/models/configurations/base-setting-scenario-configuration';
import VideoConfiguration from 'features/residents/models/configurations/video-configuration';
import WanderingDetectionConfiguration from 'features/residents/models/configurations/wandering-detection-configuration';
import OrganisationUnitScenario from 'features/residents/models/organisation-unit-scenario';
import AcousticScenarioConfiguration from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/acoustic-scenario-configuration/acoustic-scenario-configuration';
import AlarmOnlyScenarioConfiguration from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/alarm-only-scenario-configuration/alarm-only-scenario-configuration';
import AlarmScenarioConfiguration from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/alarm-scenario-configuration/alarm-scenario-configuration';
import AudioScenarioConfiguration from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/audio-scenario-configuration/audio-scenario-configuration';
import RowConfigurationAccordion from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/row-configuration-accordion/row-configuration-accordion';
import SettingScenarioConfiguration from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/setting-scenario-configuration/setting-scenario-configuration';
import VideoScenarioConfiguration from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/video-scenario-configuration/video-scenario-configuration';
import WanderingDetectionScenarioConfiguration from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/wandering-detection-scenario-configuration/wandering-detection-scenario-configuration';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type Props = {
  index: number;
  organisationUnitScenario: OrganisationUnitScenario;
};

type ComponentProps = Props & {
  title: string;
};

const getParsedConfiguration = (configuration: string | undefined): unknown => jsonParseWithLowerCaseKeys(configuration);

const RoomScenarioConfiguration = (props: Readonly<ComponentProps>) => {
  const { organisationUnitScenario, title, index } = props;
  const { t } = useTranslation('residentsSettings');
  const configuration = getParsedConfiguration(organisationUnitScenario.configuration) as BaseAlarmScenarioConfiguration;

  return (
    <RowConfigurationAccordion
      {...props}
      configuration={configuration}
      organizationUnitScenario={organisationUnitScenario}
      scenarioRowTitle={title}
      showAlarmInfo
      subtitlePrefix={t('scenarioConfiguration.scenarioActiveAllDay')}
      isAlarmActive={organisationUnitScenario.isActive && configuration.isAlarmActive}
    >
      <AlarmScenarioConfiguration scenarioTitle={title} index={index} />
    </RowConfigurationAccordion>
  );
};

const BedScenarioConfigurationComponent = (props: Readonly<ComponentProps>) => {
  const { organisationUnitScenario, title, index } = props;
  const { t } = useTranslation('residentsSettings');
  const baseConfiguration = getParsedConfiguration(organisationUnitScenario.configuration) as BaseAlarmScenarioConfiguration;
  const {
    detailEditState: { currentResidentSetting },
  } = use(ResidentSettingsProviderContext);

  return (
    <RowConfigurationAccordion
      {...props}
      configuration={baseConfiguration}
      organizationUnitScenario={organisationUnitScenario}
      scenarioRowTitle={title}
      showAlarmInfo
      subtitlePrefix={t('scenarioConfiguration.scenarioActiveAllDay')}
      isAlarmActive={organisationUnitScenario.isActive && baseConfiguration.isAlarmActive}
    >
      <AlarmScenarioConfiguration
        scenarioTitle={title}
        index={index}
        situationalOptionEnabled={currentResidentSetting?.intentionOutOfBedEnabled ?? false}
        situationalMenuItem={
          <MenuItem data-testid={`alarm-timeout-item-intentionOutOfBed`} key={`alarm-timeout-item-intentionOutOfBed`} value={-1}>
            {t('scenarioConfiguration.intentionOutOfBed')}
          </MenuItem>
        }
      />
    </RowConfigurationAccordion>
  );
};

const SettingScenarioConfigurationComponent = (props: Readonly<ComponentProps>) => {
  const { organisationUnitScenario, title, index } = props;
  const { t } = useTranslation('residentsSettings');
  const settingConfiguration = getParsedConfiguration(organisationUnitScenario.configuration) as BaseSettingScenarioConfiguration;

  return (
    <RowConfigurationAccordion
      {...props}
      configuration={settingConfiguration}
      organizationUnitScenario={organisationUnitScenario}
      scenarioRowTitle={title}
      subtitlePrefix={`${t('scenarioConfiguration.scenarioAvailable')} ${
        settingConfiguration.whenActiveAlarm ? t('scenarioConfiguration.settingScenarioWhenActiveAlarm') : ''
      }`}
      whenActiveAlarm={settingConfiguration.whenActiveAlarm}
      isAlarmActive={organisationUnitScenario.isActive}
    >
      <SettingScenarioConfiguration scenarioTitle={title} index={index} />
    </RowConfigurationAccordion>
  );
};

const VideoScenarioConfigurationComponent = (props: Readonly<ComponentProps>) => {
  const { organisationUnitScenario, title, index } = props;
  const { t } = useTranslation('residentsSettings');
  const settingConfiguration = getParsedConfiguration(organisationUnitScenario.configuration) as VideoConfiguration;

  return (
    <RowConfigurationAccordion
      {...props}
      configuration={settingConfiguration}
      organizationUnitScenario={organisationUnitScenario}
      scenarioRowTitle={title}
      subtitlePrefix={`${t('scenarioConfiguration.scenarioAvailable')} ${
        settingConfiguration.whenActiveAlarm ? t('scenarioConfiguration.settingScenarioWhenActiveAlarm') : ''
      }`}
      whenActiveAlarm={settingConfiguration.whenActiveAlarm}
      isAlarmActive={organisationUnitScenario.isActive}
    >
      <VideoScenarioConfiguration scenarioTitle={title} index={index} />
    </RowConfigurationAccordion>
  );
};

const AudioScenarioConfigurationComponent = (props: Readonly<ComponentProps>) => {
  const { organisationUnitScenario, title, index } = props;
  const { t } = useTranslation('residentsSettings');
  const settingConfiguration = getParsedConfiguration(organisationUnitScenario.configuration) as AudioConfiguration;

  return (
    <RowConfigurationAccordion
      {...props}
      configuration={settingConfiguration}
      organizationUnitScenario={organisationUnitScenario}
      scenarioRowTitle={title}
      subtitlePrefix={`${t('scenarioConfiguration.scenarioAvailable')} ${
        settingConfiguration.whenActiveAlarm ? t('scenarioConfiguration.settingScenarioWhenActiveAlarm') : ''
      }`}
      whenActiveAlarm={settingConfiguration.whenActiveAlarm}
      isAlarmActive={organisationUnitScenario.isActive}
    >
      <AudioScenarioConfiguration scenarioTitle={title} index={index} />
    </RowConfigurationAccordion>
  );
};

const AcousticScenarioConfigurationComponent = (props: Readonly<ComponentProps>) => {
  const { organisationUnitScenario, title, index } = props;
  const { t } = useTranslation('residentsSettings');
  const acousticConfiguration = getParsedConfiguration(organisationUnitScenario.configuration) as AcousticConfiguration;

  return (
    <RowConfigurationAccordion
      {...props}
      configuration={acousticConfiguration}
      organizationUnitScenario={organisationUnitScenario}
      scenarioRowTitle={title}
      showAlarmInfo
      subtitlePrefix={t('scenarioConfiguration.scenarioActiveAllDay')}
      isAlarmActive={organisationUnitScenario.isActive}
    >
      <AcousticScenarioConfiguration scenarioTitle={title} index={index} />
    </RowConfigurationAccordion>
  );
};

const AlarmOnlyScenarioConfigurationComponent = (props: Readonly<ComponentProps>) => {
  const { organisationUnitScenario, title, index } = props;
  const { t } = useTranslation('residentsSettings');
  const configuration = getParsedConfiguration(organisationUnitScenario.configuration) as BaseAlarmOnlyScenarioConfiguration;

  return (
    <RowConfigurationAccordion
      {...props}
      configuration={configuration}
      organizationUnitScenario={organisationUnitScenario}
      scenarioRowTitle={title}
      showAlarmInfo
      isAlarmActive={organisationUnitScenario.isActive}
      subtitlePrefix={t('scenarioConfiguration.scenarioActiveAllDay')}
    >
      <AlarmOnlyScenarioConfiguration scenarioTitle={title} index={index} />
    </RowConfigurationAccordion>
  );
};

const WanderingDetectionScenarioConfigurationComponent = (props: Readonly<ComponentProps>) => {
  const { organisationUnitScenario, title, index } = props;
  const { t } = useTranslation('residentsSettings');
  const wanderingDetectionConfiguration = getParsedConfiguration(organisationUnitScenario.configuration) as WanderingDetectionConfiguration;

  return (
    <RowConfigurationAccordion
      {...props}
      configuration={wanderingDetectionConfiguration}
      organizationUnitScenario={organisationUnitScenario}
      scenarioRowTitle={title}
      showAlarmInfo
      subtitlePrefix={t('scenarioConfiguration.scenarioActiveAllDay')}
      isAlarmActive={organisationUnitScenario.isActive}
    >
      <WanderingDetectionScenarioConfiguration scenarioTitle={title} index={index} />
    </RowConfigurationAccordion>
  );
};

const ResidentSettingsDetailsScenarioRow = (props: Readonly<Props>): React.JSX.Element | null => {
  const { organisationUnitScenario, index } = props;
  const { t } = useTranslation('residentsSettings');

  const {
    detailEditState: { currentResidentSetting },
  } = use(ResidentSettingsProviderContext);

  if (!currentResidentSetting?.organisationUnitScenarios[index]?.configuration) {
    return null;
  }

  const scenarioComponents: { [key in ScenarioType]?: (props: ComponentProps) => React.JSX.Element } = {
    [ScenarioType.CurrentLocation]: SettingScenarioConfigurationComponent,
    [ScenarioType.Bed]: BedScenarioConfigurationComponent,
    [ScenarioType.Room]: RoomScenarioConfiguration,
    [ScenarioType.Intruder]: AlarmOnlyScenarioConfigurationComponent,
    [ScenarioType.Bathroom]: RoomScenarioConfiguration,
    [ScenarioType.Door]: AlarmOnlyScenarioConfigurationComponent,
    [ScenarioType.FallDetection]: AlarmOnlyScenarioConfigurationComponent,
    [ScenarioType.Video]: VideoScenarioConfigurationComponent,
    [ScenarioType.Audio]: AudioScenarioConfigurationComponent,
    [ScenarioType.Acoustic]: AcousticScenarioConfigurationComponent,
    [ScenarioType.WanderingDetection]: WanderingDetectionScenarioConfigurationComponent,
  };

  const scenarioTitles: { [key in ScenarioType]?: string } = {
    [ScenarioType.CurrentLocation]: t('details.currentLocation'),
    [ScenarioType.Bed]: t('details.leftBed'),
    [ScenarioType.Room]: t('details.leftRoom'),
    [ScenarioType.Intruder]: t('details.intruder'),
    [ScenarioType.Bathroom]: t('details.bathroom'),
    [ScenarioType.Door]: t('details.door'),
    [ScenarioType.FallDetection]: t('details.fallDetection'),
    [ScenarioType.Video]: t('details.video'),
    [ScenarioType.Audio]: t('details.audio'),
    [ScenarioType.Acoustic]: t('details.acoustic'),
    [ScenarioType.WanderingDetection]: t('details.wanderingDetection'),
  };

  const ScenarioComponent = scenarioComponents[organisationUnitScenario.scenarioType];
  const title = scenarioTitles[organisationUnitScenario.scenarioType];

  if (ScenarioComponent && title) {
    return (
      <div>
        <ScenarioComponent {...props} title={title} />
      </div>
    );
  }

  return null;
};

export default ResidentSettingsDetailsScenarioRow;
